/**
 * SEO_BRIEF_11 section 2.1, /audio-cutter: the arithmetic of a cut, with no media in it, so the
 * tests read every rule directly and the worker (src/media/audio-cut.ts) and the page
 * (web/static/audio/src/audio-cut.ts) share one copy of it.
 *
 *   the selection     start and end handles on the sound's own timeline, in seconds, never
 *                     closer than MIN_SELECTION_S, never outside 0 … duration; a nudge is a move
 *                     by NUDGE_S, held to the same rules
 *   the trim          which frames of one decoded chunk fall inside the selection (`keepRange`)
 *   the fades         a straight ramp over FADE_S at the start and/or the end, shorter when the
 *                     selection is short (`fadeLengths`, `gainAt`, `applyGain`)
 *   the waveform      the loudest point in each slice of time (`PeakBuilder`)
 *   the WAV           16-bit PCM, little-endian, the plain RIFF header every player opens
 *   the names         `song.m4a` → `song-cut.mp3`; times as `1:02.3`
 *   the outputs       MP3 at 128, 192 or 320 kbit/s, or WAV
 */

/** The shortest selection the page saves, in seconds. */
export const MIN_SELECTION_S = 0.1;
/** One tap of a −0.1 s / +0.1 s button. */
export const NUDGE_S = 0.1;
/** How long a fade lasts when the selection is long enough. */
export const FADE_S = 2;
/** The waveform's finest slices per second, and the most slices it keeps for a long file. */
export const MAX_PEAK_RATE = 100;
export const MAX_PEAKS = 120_000;
/**
 * The longest stretch "Play the selection" makes into a WAV of its own, for a file the browser's
 * player cannot play itself (an MKV in Safari): five minutes of 48 kHz stereo is about 58 MB,
 * which a phone holds. A longer selection plays its first five minutes there.
 */
export const PREVIEW_MAX_S = 300;

/** What Save hands back: an MP3 at one of three rates, or WAV. */
export const CUT_OUTPUTS = ['mp3-128', 'mp3-192', 'mp3-320', 'wav'] as const;
export type CutOutput = (typeof CUT_OUTPUTS)[number];
/** The output a page opens on. */
export const DEFAULT_OUTPUT: CutOutput = 'mp3-192';

/** The MP3 rate of an output, or null for WAV. */
export function outputKbps(output: CutOutput): 128 | 192 | 320 | null {
  switch (output) {
    case 'mp3-128':
      return 128;
    case 'mp3-192':
      return 192;
    case 'mp3-320':
      return 320;
    case 'wav':
      return null;
  }
}

/** A value from the page's select, as an output; anything else is the default. */
export function parseOutput(value: unknown): CutOutput {
  return (CUT_OUTPUTS as readonly unknown[]).includes(value)
    ? (value as CutOutput)
    : DEFAULT_OUTPUT;
}

export interface Selection {
  readonly start: number;
  readonly end: number;
}

export type Handle = 'start' | 'end';

export interface Fades {
  /** Seconds of fade-in from the selection's start; 0 for none. */
  readonly in: number;
  /** Seconds of fade-out to the selection's end; 0 for none. */
  readonly out: number;
}

/** Milliseconds are as fine as any handle is placed, and keep 0.1 + 0.2 from showing. */
const ms = (seconds: number): number => Math.round(seconds * 1000) / 1000;

const clamp = (v: number, lo: number, hi: number): number => (v < lo ? lo : v > hi ? hi : v);

/** The whole sound selected: where the page starts once a file is read. */
export function wholeSelection(duration: number): Selection {
  return { start: 0, end: ms(Math.max(0, duration)) };
}

/** A selection held inside 0 … duration with at least MIN_SELECTION_S between the handles. */
export function clampSelection(sel: Selection, duration: number): Selection {
  const total = Math.max(0, duration);
  if (total <= MIN_SELECTION_S) return { start: 0, end: ms(total) };
  const start = clamp(Number.isFinite(sel.start) ? sel.start : 0, 0, total - MIN_SELECTION_S);
  const end = clamp(Number.isFinite(sel.end) ? sel.end : total, start + MIN_SELECTION_S, total);
  return { start: ms(start), end: ms(end) };
}

/**
 * One handle moved to `to`, stopped by the other handle (MIN_SELECTION_S short of it) and by the
 * ends of the sound. The other handle never moves.
 */
export function moveHandle(sel: Selection, which: Handle, to: number, duration: number): Selection {
  const total = Math.max(0, duration);
  if (!Number.isFinite(to)) return sel;
  if (which === 'start') {
    return { start: ms(clamp(to, 0, Math.max(0, sel.end - MIN_SELECTION_S))), end: sel.end };
  }
  return {
    start: sel.start,
    end: ms(clamp(to, Math.min(total, sel.start + MIN_SELECTION_S), total)),
  };
}

/** A −0.1 s / +0.1 s tap (`delta` = ∓NUDGE_S), by the same rules as a drag. */
export function nudgeHandle(
  sel: Selection,
  which: Handle,
  delta: number,
  duration: number,
): Selection {
  return moveHandle(sel, which, sel[which] + delta, duration);
}

/** How long the selection is, in seconds. */
export function selectionLength(sel: Selection): number {
  return ms(Math.max(0, sel.end - sel.start));
}

/** The part of the selection a played WAV covers: all of it, or its first PREVIEW_MAX_S. */
export function previewSelection(sel: Selection): Selection {
  return { start: sel.start, end: ms(Math.min(sel.end, sel.start + PREVIEW_MAX_S)) };
}

/**
 * The fades a cut gets: FADE_S each where asked for, and never more than half the selection, so a
 * fade-in and a fade-out on a short cut meet in the middle rather than overlap.
 */
export function fadeLengths(
  sel: Selection,
  fadeIn: boolean,
  fadeOut: boolean,
  fadeS: number = FADE_S,
): Fades {
  const half = selectionLength(sel) / 2;
  const each = Math.max(0, Math.min(fadeS, half));
  return { in: fadeIn ? each : 0, out: fadeOut ? each : 0 };
}

/** The loudness, 0 to 1, at time `t` inside the selection: a straight ramp up, then down. */
export function gainAt(t: number, sel: Selection, fades: Fades): number {
  let gain = 1;
  if (fades.in > 0) {
    const into = t - sel.start;
    if (into < fades.in) gain *= Math.max(0, into) / fades.in;
  }
  if (fades.out > 0) {
    const left = sel.end - t;
    if (left < fades.out) gain *= Math.max(0, left) / fades.out;
  }
  return clamp(gain, 0, 1);
}

/**
 * The fades applied in place to one chunk of decoded sound, whose first frame plays at
 * `firstFrameTime`. Frames outside both ramps are left exactly as they are.
 */
export function applyGain(
  planes: readonly Float32Array[],
  firstFrameTime: number,
  sampleRate: number,
  sel: Selection,
  fades: Fades,
): void {
  const first = planes[0];
  if (first === undefined || (fades.in <= 0 && fades.out <= 0)) return;
  const frames = first.length;
  const chunkEnd = firstFrameTime + frames / sampleRate;
  const touchesIn = fades.in > 0 && firstFrameTime < sel.start + fades.in;
  const touchesOut = fades.out > 0 && chunkEnd > sel.end - fades.out;
  if (!touchesIn && !touchesOut) return;
  for (let i = 0; i < frames; i++) {
    const gain = gainAt(firstFrameTime + i / sampleRate, sel, fades);
    if (gain === 1) continue;
    for (const plane of planes) plane[i] = (plane[i] ?? 0) * gain;
  }
}

/**
 * Which frames of a chunk fall inside the selection: `[from, to)`, empty when `from === to`.
 * Rounded to the nearest frame, so two chunks that meet exactly split a frame the same way.
 */
export function keepRange(
  chunkStart: number,
  frames: number,
  sampleRate: number,
  sel: Selection,
): { from: number; to: number } {
  const from = clamp(Math.round((sel.start - chunkStart) * sampleRate), 0, frames);
  const to = clamp(Math.round((sel.end - chunkStart) * sampleRate), from, frames);
  return { from, to };
}

/** The waveform's slices per second for a sound this long (or of no known length). */
export function peakRateFor(duration: number | null): number {
  if (duration === null || !Number.isFinite(duration) || duration <= 0) return 50;
  return clamp(Math.floor(MAX_PEAKS / duration), 1, MAX_PEAK_RATE);
}

/**
 * The waveform, built as the sound is decoded: for each slice of 1/rate seconds, the loudest
 * sample on any channel (0 to 1). Grows as it goes, so a sound of no known length still fits.
 */
export class PeakBuilder {
  readonly rate: number;
  private peaks: Float32Array;
  private used = 0;

  constructor(rate: number, expectedSlices = 0) {
    this.rate = rate;
    this.peaks = new Float32Array(Math.max(64, Math.ceil(expectedSlices) + 1));
  }

  add(planes: readonly Float32Array[], firstFrameTime: number, sampleRate: number): void {
    const first = planes[0];
    if (first === undefined) return;
    const step = this.rate / sampleRate;
    const base = firstFrameTime * this.rate;
    for (let i = 0; i < first.length; i++) {
      const slot = Math.floor(base + i * step);
      if (slot < 0) continue;
      let loud = 0;
      for (const plane of planes) {
        const v = Math.abs(plane[i] ?? 0);
        if (v > loud) loud = v;
      }
      if (slot >= this.peaks.length) this.grow(slot + 1);
      if (loud > (this.peaks[slot] ?? 0)) this.peaks[slot] = loud > 1 ? 1 : loud;
      if (slot + 1 > this.used) this.used = slot + 1;
    }
  }

  private grow(atLeast: number): void {
    const next = new Float32Array(Math.max(atLeast, this.peaks.length * 2));
    next.set(this.peaks);
    this.peaks = next;
  }

  /** The slices so far, as a fresh array the size of the sound. */
  result(): Float32Array {
    return this.peaks.slice(0, this.used);
  }
}

/**
 * 16-bit PCM WAV, written as the cut is decoded: chunks in, one file out, a `Blob` built from the
 * chunks themselves, so a long cut is never copied into one buffer first (as audio-convert.ts
 * `wavSink` does). Values beyond full scale are clipped, as any player would.
 */
export class WavWriter {
  readonly sampleRate: number;
  readonly channels: 1 | 2;
  private chunks: Uint8Array<ArrayBuffer>[] = [];
  private size = 0;

  constructor(sampleRate: number, channels: 1 | 2) {
    this.sampleRate = sampleRate;
    this.channels = channels;
  }

  get frames(): number {
    return this.size / (2 * this.channels);
  }

  push(planes: readonly Float32Array[]): void {
    const first = planes[0];
    if (first === undefined || first.length === 0) return;
    const frames = first.length;
    const out = new Int16Array(frames * this.channels);
    for (let i = 0; i < frames; i++) {
      for (let c = 0; c < this.channels; c++) {
        const plane = planes[c] ?? first;
        const v = plane[i] ?? 0;
        const s = v > 1 ? 1 : v < -1 ? -1 : v;
        out[i * this.channels + c] = Math.round(s < 0 ? s * 0x8000 : s * 0x7fff);
      }
    }
    // Int16Array is in the platform's order; every platform a browser runs on is little-endian,
    // as WAV is. The header below is written explicitly little-endian either way.
    const bytes = new Uint8Array(out.buffer);
    this.chunks.push(bytes);
    this.size += bytes.length;
  }

  finish(): Blob {
    const header = wavHeader(this.sampleRate, this.channels, this.size);
    const file = new Blob([header, ...this.chunks] as Uint8Array<ArrayBuffer>[], {
      type: 'audio/wav',
    });
    this.chunks = [];
    return file;
  }
}

/** The 44-byte RIFF/WAVE header for `dataBytes` of 16-bit PCM. */
export function wavHeader(sampleRate: number, channels: number, dataBytes: number): Uint8Array {
  const header = new Uint8Array(44);
  const view = new DataView(header.buffer);
  const text = (at: number, s: string) => {
    for (let i = 0; i < s.length; i++) header[at + i] = s.charCodeAt(i);
  };
  const blockAlign = channels * 2;
  text(0, 'RIFF');
  view.setUint32(4, 36 + dataBytes, true);
  text(8, 'WAVE');
  text(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true); // PCM
  view.setUint16(22, channels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * blockAlign, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, 16, true);
  text(36, 'data');
  view.setUint32(40, dataBytes, true);
  return header;
}

/** `holiday.mp4` → `holiday-cut.mp3`; a name with no stem → `audio-cut.mp3`. */
export function cutName(inputName: string, ext: 'mp3' | 'wav'): string {
  const base = inputName.split(/[\\/]/).pop() ?? '';
  const dot = base.lastIndexOf('.');
  const stem = (dot > 0 ? base.slice(0, dot) : base).trim();
  return `${stem || 'audio'}-cut.${ext}`;
}

/**
 * A time as the page shows it, to the tenth of a second the nudges move by: `0:05.3`, `12:00.0`,
 * `1:02:03.4`. Negative and non-finite times show as `0:00.0`.
 */
export function clockTenths(seconds: number): string {
  const tenths = Number.isFinite(seconds) && seconds > 0 ? Math.round(seconds * 10) : 0;
  const t = tenths % 10;
  const whole = Math.floor(tenths / 10);
  const s = whole % 60;
  const m = Math.floor(whole / 60) % 60;
  const h = Math.floor(whole / 3600);
  const ss = String(s).padStart(2, '0');
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${ss}.${t}` : `${m}:${ss}.${t}`;
}
